import React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import { Tabs, TabsProps } from 'antd/es';
import queryString from 'query-string';

import { ItemsProvider, useItemsProviderContext } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';

import CustomRequestProvider from '@app/providers/CustomRequestProvider/CustomRequestProvider';
import GetInTouchButton from '@app/components/UI/Forms/GetInTouch/GetInTouchButton';
import SearchField from '@app/components/UI/Forms/SearchField/SearchField';
import AreaNotFound from '@app/components/Pages/AreasList/AreaNotFound';
import CustomAvatar from '@app/components/UI/CustomAvatar/CustomAvatar';
import HelmetProvider from '@app/providers/HelmetProvider/HelmetProvider';
import { ScreenWidth, useIsMobile } from '@app/hooks/useIsMobile';
import { Area } from '@app/objects/Area';
import '@app/scss/components/areasList.scss';

interface AreasListInnerProps {
	innerQuery: string | Array<string | null>;
	setInnerQuery: (e: string | Array<string | null>) => void;
}
interface AreasResultProps extends AreasListInnerProps {
	items: Array<Area>;
}

const AreasResult: React.FC<AreasResultProps> = ({ items, innerQuery, setInnerQuery }) => {
	const isMobile = useIsMobile(ScreenWidth.widthSM);
	const vetsInAreasCount = items.length && items.map((item) => item.vetsCount).reduce((a, b) => a + b);

	const avatarHeight = 144;
	const avatarWidth = 144;

	const tabs: TabsProps['items'] = [
		{
			key: '1',
			label: 'Location',
			children: (
				<div>
					{items.length > 0 ? (
						<>
							<div className="areas-list-page__results">
								<div className="areas-list__titles-row">
									<span className="areas-list__title">
										Veterinarians in:
										{' '}
										<span className="text-capitalize">{innerQuery}</span>
									</span>
									<span className="areas-list__title">{`${vetsInAreasCount} Veterinarians found`}</span>
								</div>
								<div className="areas-list">
									{items.map((area, i) => (
										<React.Fragment key={`${i}-${area.name}`}>
											{isMobile ? (
												<Link
													to={`/find-a-vet/${area.state.split(' ').join('-')}/${area.url}/about`}
													className="areas-list__item area-preview"
													key={`${i}-${area.name}`}
												>
													<div className="area-preview__img-wrap">
														<CustomAvatar
															src={area.avatar}
															maxHeight={avatarHeight}
															maxWidth={avatarWidth}
															alt={area.name}
														/>
													</div>
													<div className="area-preview__info">
														<p className="area-preview__location">
															<span>{`${area.state}: `}</span>
															{area.name}
														</p>
														<p className="area-preview__contact">
															<span className="area-preview__vets-count">
																{`${area.vetsCount} Veterinarian${area.vetsCount > 1 ? 's' : ''}`}
															</span>
															<span className="area-preview__phone">
																Phone:
																{' '}
																<a href={`tel:${area.phoneNumber}`}>{area.phoneNumber}</a>
															</span>
														</p>
													</div>
												</Link>
											) : (
												<div className="areas-list__item area-preview" key={`${i}-${area.name}`}>
													<div className="area-preview__img-wrap">
														<CustomAvatar
															src={area.avatar}
															maxHeight={avatarHeight}
															maxWidth={avatarWidth}
															alt={area.name}
														/>
													</div>
													<div className="area-preview__info">
														<p className="area-preview__location">
															<span>{`${area.state}:`}</span>
															{' '}
															{area.name}
														</p>
														<p className="area-preview__contact">
															<span className="area-preview__vets-count">
																{`${area.vetsCount} Veterinarian${area.vetsCount > 1 ? 's' : ''}`}
															</span>
															<span className="area-preview__phone">
																Phone:
																{' '}
																<a href={`tel:${area.phoneNumber}`}>{area.phoneNumber}</a>
															</span>
														</p>
													</div>
													<Link
														to={`/find-a-vet/${area.state.split(' ').join('-')}/${area.url}/about`}
														className="btn btn-sm btn-primary area-preview__btn"
													>
														View
													</Link>
												</div>
											)}
										</React.Fragment>
									))}
								</div>
							</div>

							<div className="call-us">
								<p className="call-us__text">Can't find what you're looking for?</p>
								{isMobile ? (
									<a className="btn btn-md btn-primary" href="tel:855-933-5683">
										Give us a call
									</a>
								) : (
									<GetInTouchButton btnText="Give us a call" />
								)}
							</div>
						</>
					) : (
						<AreaNotFound isMobile={isMobile} />
					)}
				</div>
			),
		},
	];

	return (
		<div className="areas-list-page">
			<div className="areas-list-page__inner">
				<div className="search-field-toggle">
					<Tabs
						type="card"
						activeKey="1"
						tabBarExtraContent={
							<SearchField
								widthBorder="0"
								setSearch={setInnerQuery}
								autoFocus
								placeholder="Enter your 5-digit ZIP code"
							/>
						}
						items={tabs}
					/>
				</div>
			</div>
		</div>
	);
};

const AreasListInner: React.FC<AreasListInnerProps> = ({ innerQuery, setInnerQuery }) => {
	const context = useItemsProviderContext<Area>();

	if (!context?.state) throw new Error('Error context');

	const {
		state: { items, loading },
		actions: { handleChange },
	} = context;

	const [initLoader, setInitLoader] = React.useState<boolean>(true);

	React.useEffect(() => {
		setInitLoader(false);
	}, []);

	React.useEffect(() => {
		handleChange();
	}, [innerQuery]);

	return (
		!initLoader && !loading ? (
			<>
				{items.length === 1 ? (
					<Navigate to={`/find-a-vet/${items[0].state.split(' ').join('-')}/${items[0].url}/about`} />
				) : (
					<AreasResult innerQuery={innerQuery} setInnerQuery={setInnerQuery} items={items} />
				)}
			</>
		) : <BounceLoader />
	);
};

const AreasList: React.FC = () => {
	const location = useLocation();
	const [innerQuery, setInnerQuery] = React.useState<string | Array<string | null>>(queryString.parse(location.search).query || '');

	React.useEffect(() => {
		setInnerQuery(queryString.parse(location.search).query || '');
	}, [location.search]);

	return (
		<HelmetProvider ignoreServerPage>
			<CustomRequestProvider>
				<ItemsProvider<Area>
					type={`areas/${innerQuery}`}
					loadRequest={`areas/${innerQuery}`}
					pagination={{ pageSize: 1000 }}
				>
					<AreasListInner innerQuery={innerQuery} setInnerQuery={setInnerQuery} />
				</ItemsProvider>
			</CustomRequestProvider>
		</HelmetProvider>
	);
};

export default AreasList;
